import { Camera, ImageUp, RotateCcw } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

type State = "idle" | "starting" | "live" | "unavailable";

/**
 * Take a photo with the tablet camera (T07-31 本人確認 / card photos): a live picture with a guide
 * frame (put the card / ID inside it), 「撮影」 → the photo; 「撮り直す」 to try again. Without a
 * camera (or when the browser refuses it) the file / camera picker of the system is offered
 * instead. The photo stays in memory until the caller uploads it — straight to media-service for
 * an ID (never to pos-service).
 */
export function CameraCapture({
  label,
  guide = "card",
  onPhoto,
  photo,
}: {
  label: string;
  guide?: "card" | "document";
  onPhoto: (photo: Blob | null) => void;
  photo: Blob | null;
}) {
  const { t } = useTranslation();
  const video = useRef<HTMLVideoElement | null>(null);
  const stream = useRef<MediaStream | null>(null);
  const [state, setState] = useState<State>("idle");
  const preview = useMemo(() => {
    if (!photo || typeof URL.createObjectURL !== "function") {
      return null;
    }
    try {
      return URL.createObjectURL(photo);
    } catch {
      return null; // a browser that cannot preview: the photo is still kept
    }
  }, [photo]);
  useEffect(
    () => () => {
      if (preview) {
        URL.revokeObjectURL(preview);
      }
    },
    [preview],
  );

  const stop = () => {
    for (const track of stream.current?.getTracks() ?? []) {
      track.stop();
    }
    stream.current = null;
  };
  useEffect(() => stop, []);

  const start = async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      setState("unavailable");
      return;
    }
    setState("starting");
    try {
      const media = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "environment", width: { ideal: 1920 } },
        audio: false,
      });
      stream.current = media;
      setState("live");
      if (video.current) {
        video.current.srcObject = media;
        await video.current.play().catch(() => undefined);
      }
    } catch {
      setState("unavailable");
    }
  };

  const shoot = async () => {
    const el = video.current;
    if (!el) {
      return;
    }
    if (!el.videoWidth) {
      // the first frame may not be there yet right after the camera opened
      await new Promise<void>((resolve) => {
        el.addEventListener("loadeddata", () => resolve(), { once: true });
        setTimeout(resolve, 1500);
      });
    }
    if (!el.videoWidth) {
      return;
    }
    const canvas = document.createElement("canvas");
    canvas.width = el.videoWidth;
    canvas.height = el.videoHeight;
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      return;
    }
    ctx.drawImage(el, 0, 0);
    canvas.toBlob(
      (blob) => {
        if (blob) {
          onPhoto(blob);
          stop();
          setState("idle");
        }
      },
      "image/jpeg",
      0.9,
    );
  };

  return (
    <div className="flex flex-col gap-2">
      <span className="font-medium">{label}</span>
      {photo && preview ? (
        <img
          src={preview}
          alt={t("camera.taken", { label })}
          className="max-h-56 w-full rounded-md border object-contain"
        />
      ) : photo ? (
        <p className="rounded-md border p-3">{t("camera.taken", { label })}</p>
      ) : null}
      {state === "live" || state === "starting" ? (
        <div className="relative overflow-hidden rounded-md bg-black">
          {/* the camera picture; the frame shows where the card / ID goes */}
          <video ref={video} muted playsInline className="max-h-72 w-full object-contain" />
          <div
            aria-hidden
            className={
              guide === "card"
                ? "pointer-events-none absolute inset-x-[12%] inset-y-[18%] rounded-lg border-4 border-dashed border-white/90"
                : "pointer-events-none absolute inset-x-[6%] inset-y-[8%] rounded-lg border-4 border-dashed border-white/90"
            }
          />
          <p className="absolute inset-x-0 bottom-2 text-center text-white">{t("camera.guide")}</p>
        </div>
      ) : null}
      <div className="flex flex-wrap gap-2">
        {state === "live" ? (
          <button
            type="button"
            onClick={() => void shoot()}
            className="flex min-h-pos-touch items-center gap-2 rounded-md bg-primary px-6 text-lg font-semibold text-primary-foreground"
          >
            <Camera aria-hidden className="size-6" />
            {t("camera.shoot")}
          </button>
        ) : state !== "unavailable" ? (
          <button
            type="button"
            onClick={() => void start()}
            className="flex min-h-pos-touch items-center gap-2 rounded-md border px-5 text-lg"
          >
            {photo ? (
              <RotateCcw aria-hidden className="size-5" />
            ) : (
              <Camera aria-hidden className="size-5" />
            )}
            {photo ? t("camera.retake") : t("camera.open")}
          </button>
        ) : null}
        <label className="flex min-h-pos-touch cursor-pointer items-center gap-2 rounded-md border px-5 text-lg">
          <ImageUp aria-hidden className="size-5" />
          {state === "unavailable" ? t("camera.no_camera") : t("camera.file")}
          <input
            type="file"
            accept="image/*"
            capture="environment"
            className="sr-only"
            aria-label={`${label} — ${t("camera.file")}`}
            onChange={(e) => {
              const file = e.target.files?.[0] ?? null;
              if (file) {
                onPhoto(file);
              }
              e.target.value = "";
            }}
          />
        </label>
      </div>
    </div>
  );
}
